import { fetchDataFromCookies } from '@/services/util/cookies.util';
import { Product } from '@/types';
import { CartItem, CartResponse } from '@/types/cart.types';

import { getSessionData } from './analytics.util';
import { SESSION_KEYS } from './constants';

export function extractCartQuantities(
  data: CartResponse | undefined | null
): Record<string, number> {
  if (!data?.items) {
    return {};
  }

  return Object.values(data.items).reduce(
    (acc, item) => {
      if (!item.productId) return acc;

      const value =
        typeof item.quantity === 'number'
          ? item.quantity
          : typeof item.weight === 'number'
            ? item.weight
            : 0;

      acc[item.productId] = value;

      return acc;
    },
    {} as Record<string, number>
  );
}

export function parseStoreId(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    if (!trimmedValue) return null;

    const numericValue = Number(trimmedValue);
    return Number.isFinite(numericValue) ? numericValue : null;
  }

  return null;
}

export function getFallbackStoreId(): number {
  return parseStoreId(process.env.NEXT_PUBLIC_STORE_ID) ?? 5001;
}

export function getStoreId(storeId?: string | number | null): number {
  return parseStoreId(storeId) ?? getFallbackStoreId();
}

export const getStepValue = (product: Product) => {
  const isWeightBased = product.itemSoldBy?.toLowerCase() === 'weight';
  const weightStep = Number(product.weight);

  return isWeightBased && weightStep > 0 ? weightStep : 1;
};

export const getCookieOrSessionId = () => {
  const cookieData = fetchDataFromCookies();

  const value = cookieData.storeId;

  if (typeof value === 'number') return value;

  return (
    parseStoreId(value) ??
    Number(getSessionData(SESSION_KEYS.STORE_ID)) ??
    getFallbackStoreId()
  );
};

function lineUnits(item: Partial<CartItem> | null | undefined): number {
  if (!item) return 0;
  if (item.type === 'weight') {
    const w = item.weight;
    return typeof w === 'number' && Number.isFinite(w) && w > 0 ? 1 : 0;
  }
  const q = item.quantity;
  return typeof q === 'number' && Number.isFinite(q) ? q : 0;
}

export function getCartItemCount(
  cart: (CartResponse & { data?: CartResponse }) | null
): number {
  const normalizedCart = cart?.data ?? cart;
  if (!normalizedCart) return 0;

  const { items } = normalizedCart;
  if (Array.isArray(items)) {
    return items.reduce(
      (sum: number, item: CartItem | null | undefined) => sum + lineUnits(item),
      0
    );
  }
  if (items && typeof items === 'object') {
    return Object.values(
      items as Record<string, Partial<CartItem> | null | undefined>
    ).reduce((sum, item) => sum + lineUnits(item), 0);
  }
  if (typeof normalizedCart.itemCount === 'number') {
    return normalizedCart.itemCount;
  }
  return 0;
}
